// Геометрия Ганта отчёта о пропусках (чистая): строки, уровни источников, ось времени.
//
// Строка — судно; в строке у каждого источника свой уровень и цвет (1 сверху,
// 2 посередине, 3 снизу — решение владельца 03.11.2026). Бусинка — точка АИС
// источника, полоса его цвета с треугольными концами — пропуск источника, фон
// строки — общий пропуск (молчали все источники). Время вне окна источника —
// штриховка: там он ничего не мог видеть. Под судном — соседи с сигналом во
// время его пропусков, янтарные. Полосы приходят с сервера готовыми
// (`gap_view.payload`, `gap_combine`), определение пропуска здесь не повторяется.

import type { AisGap, AisGapResult } from '../api'

/** Цвет источника по номеру: те же, что в сверке источников 03.11.2026. */
export const SOURCE_COLORS: Record<number, string> = { 1: '#1f77b4', 2: '#ff7f0e', 3: '#2ca02c' }
/** Цвет уровня отчёта без источников (старый отчёт из одного задания). */
export const BASE_COLOR = '#38bdf8'

/** Цвета судов на карте: без красного (пропуск) и без янтарного (соседи). */
export const VESSEL_COLORS = [
  '#38bdf8', '#34d399', '#a78bfa', '#f472b6', '#22d3ee', '#a3e635',
  '#818cf8', '#2dd4bf', '#e879f9', '#60a5fa', '#4ade80', '#c084fc',
]
export const NEIGHBOR_COLOR = '#fbbf24'
export const GAP_COLOR = '#ef4444'
export const PORT_GAP_COLOR = '#94a3b8'

/** Источник отчёта для Ганта: номер, окно его задания и результат. */
export interface SourceLane {
  no: number
  window: [number, number]
  result: AisGapResult
  /** Ещё результаты с соседями из сети этого источника (общий пропуск). */
  more?: AisGapResult[]
}

/** Уровень строки судна: точки и пропуски одного источника. */
export interface Lane {
  no: number
  color: string
  stamps: number[]
  /** Пропуски источника дольше порога, [начало, конец]. */
  gaps: [number, number][]
  window: [number, number]
  points: number
}

export interface GanttRow {
  key: string
  kind: 'vessel' | 'neighbor'
  mmsi: number
  name: string | null
  /** Сосед: ближайшее расстояние до пропусков судна, км. */
  km?: number
  color: string
  stamps: number[]
  /** Общие пропуски судна (у отчёта без источников — пропуски единственного). */
  gaps: AisGap[]
  /** Судно: уровни источников сверху вниз. */
  lanes?: Lane[]
  status?: string
  /** Судно: сколько у него соседей с сигналом (видно и в свёрнутом виде). */
  neighbors?: number
}

function lanesOf(mmsi: number, base: AisGapResult, sources: SourceLane[]): Lane[] {
  const list = sources.length ? sources
    : [{ no: 0, window: base.window, result: base }]
  return list.map(s => {
    const v = s.result.vessels.find(x => x.mmsi === mmsi)
    return {
      no: s.no, color: SOURCE_COLORS[s.no] ?? BASE_COLOR,
      stamps: (v?.track ?? []).map(p => p[0]),
      gaps: s.result.gaps.filter(g => g.mmsi === mmsi).map(g => [g.start, g.end] as [number, number]),
      window: s.window, points: v?.points ?? 0,
    }
  })
}

/**
 * Соседи судна по источникам. Общий пропуск лежит внутри пропуска каждого
 * источника, поэтому сосед берётся из пропусков источника, задевающих общий,
 * и только с сигналом в этом источнике во время общего пропуска. Уровень и
 * цвет у соседа — источника, где он найден; уровней столько же, сколько у судна.
 */
function sourceNeighbors(mmsi: number, gaps: AisGap[], sources: SourceLane[]): GanttRow[] {
  const found = new Map<number, { km: number; name: string | null; tracks: Map<number, Set<number>> }>()
  for (const s of sources) {
    for (const res of [s.result, ...(s.more ?? [])]) {
      const own = res.gaps.filter(g => g.mmsi === mmsi)
      for (const g of gaps) {
        for (const sg of own) {
          if (sg.end <= g.start || sg.start >= g.end) continue
          for (const n of sg.neighbors) {
            const nb = res.neighbors[String(n.mmsi)]
            const track = nb?.track ?? []
            if (!track.some(p => p[0] >= g.start && p[0] <= g.end)) continue
            const f = found.get(n.mmsi) ?? { km: Infinity, name: null, tracks: new Map<number, Set<number>>() }
            f.km = Math.min(f.km, n.km)
            f.name = f.name ?? nb?.name ?? null
            const set = f.tracks.get(s.no) ?? new Set<number>()
            for (const p of track) set.add(p[0])
            f.tracks.set(s.no, set)
            found.set(n.mmsi, f)
          }
        }
      }
    }
  }
  return [...found].sort((a, b) => a[1].km - b[1].km).map(([nb, f]) => ({
    key: `n${nb}`, kind: 'neighbor' as const, mmsi: nb, name: f.name, km: f.km,
    color: NEIGHBOR_COLOR, stamps: [], gaps: [],
    lanes: sources.map(s => {
      const stamps = [...(f.tracks.get(s.no) ?? [])].sort((a, b) => a - b)
      return { no: s.no, color: SOURCE_COLORS[s.no] ?? BASE_COLOR, stamps, gaps: [],
               window: s.window, points: stamps.length }
    }),
  }))
}

export function vesselColors(result: AisGapResult): Map<number, string> {
  return new Map(result.vessels.map((v, i) => [v.mmsi, VESSEL_COLORS[i % VESSEL_COLORS.length]]))
}

/** Строки Ганта: судно, затем его соседи с сигналом по возрастанию расстояния.
 *  ``result`` — общий пропуск (или единственный источник), ``sources`` — уровни;
 *  ``open`` — суда, чьи соседи развёрнуты (аккордеон); не задан — все. */
export function ganttRows(result: AisGapResult, open?: ReadonlySet<number>,
                          sources: SourceLane[] = []): GanttRow[] {
  const colors = vesselColors(result)
  const rows: GanttRow[] = []
  for (const v of result.vessels) {
    const gaps = result.gaps.filter(g => g.mmsi === v.mmsi)
    if (sources.length) {
      const nbs = sourceNeighbors(v.mmsi, gaps, sources)
      rows.push({ key: `v${v.mmsi}`, kind: 'vessel', mmsi: v.mmsi, name: v.name,
                  color: colors.get(v.mmsi)!, stamps: v.track.map(p => p[0]), gaps, status: v.status,
                  neighbors: nbs.length, lanes: lanesOf(v.mmsi, result, sources) })
      if (!open || open.has(v.mmsi)) rows.push(...nbs.map(n => ({ ...n, key: `v${v.mmsi}n${n.mmsi}` })))
      continue
    }
    const near = new Map<number, number>()
    for (const g of gaps) {
      for (const n of g.neighbors) near.set(n.mmsi, Math.min(near.get(n.mmsi) ?? Infinity, n.km))
    }
    rows.push({ key: `v${v.mmsi}`, kind: 'vessel', mmsi: v.mmsi, name: v.name,
                color: colors.get(v.mmsi)!, stamps: v.track.map(p => p[0]), gaps, status: v.status,
                neighbors: near.size, lanes: lanesOf(v.mmsi, result, sources) })
    if (open && !open.has(v.mmsi)) continue
    for (const [mmsi, km] of [...near].sort((a, b) => a[1] - b[1])) {
      const nb = result.neighbors[String(mmsi)]
      rows.push({ key: `v${v.mmsi}n${mmsi}`, kind: 'neighbor', mmsi, name: nb?.name ?? null, km,
                  color: NEIGHBOR_COLOR, stamps: (nb?.track ?? []).map(p => p[0]), gaps: [] })
    }
  }
  return rows
}

/** Секунды эпохи → пиксели внутри [x0, x1]. */
export function scaleX(window: [number, number], x0: number, x1: number) {
  const [t0, t1] = window
  const k = (x1 - x0) / Math.max(1, t1 - t0)
  return (t: number) => x0 + (Math.min(Math.max(t, t0), t1) - t0) * k
}

/** Шаги делений, с: от 10 минут (сильное приближение) до недели. */
const STEPS_S = [600, 1800, 3600, 3 * 3600, 6 * 3600, 12 * 3600, 86400, 2 * 86400, 7 * 86400]

/** Деления оси: круглые моменты UTC с шагом, при котором делений не больше `max`. */
export function timeTicks(window: [number, number], max = 8): number[] {
  const [t0, t1] = window
  const step = STEPS_S.find(s => Math.floor((t1 - t0) / s) + 1 <= max) ?? 7 * 86400
  const out: number[] = []
  for (let t = Math.ceil(t0 / step) * step; t <= t1; t += step) out.push(t)
  return out
}

/** Засечки строки одним путём SVG; засечки в один пиксель сливаются. */
export function tickPath(stamps: number[], x: (t: number) => number, y: number, h: number): string {
  const seen = new Set<number>()
  let d = ''
  for (const t of stamps) {
    const px = Math.round(x(t) * 2) / 2
    if (seen.has(px)) continue
    seen.add(px)
    d += `M${px} ${y}v${h}`
  }
  return d
}

/** Подпись деления оси: «24.10» для суток, «24.10 06:00» для часов — коротко, UTC. */
export function tickLabel(t: number, daily: boolean): string {
  const d = new Date(t * 1000)
  const p = (n: number) => String(n).padStart(2, '0')
  const day = `${p(d.getUTCDate())}.${p(d.getUTCMonth() + 1)}`
  return daily ? day : `${day} ${p(d.getUTCHours())}:${p(d.getUTCMinutes())}`
}

/** Середины уровней источников в строке высотой `h` от `y`: n уровней поровну. */
export function sourceLevels(n: number, y: number, h: number): number[] {
  const step = h / (n + 1)
  return Array.from({ length: n }, (_, i) => y + step * (i + 1))
}

/** Полоса пропуска источника: прямоугольник с треугольными концами (точка
 *  источника стоит на острие), конус не длиннее трети полосы. */
export function taperPath(x0: number, x1: number, yc: number, half: number, taper = 8): string {
  const k = Math.min(taper, (x1 - x0) / 3)
  return `M${x0} ${yc}L${x0 + k} ${yc - half}L${x1 - k} ${yc - half}L${x1} ${yc}`
    + `L${x1 - k} ${yc + half}L${x0 + k} ${yc + half}Z`
}

/** Куски окна отчёта, которые окно источника не покрывает: там — штриховка. */
export function uncovered(report: [number, number], source: [number, number]): [number, number][] {
  const out: [number, number][] = []
  if (source[0] > report[0]) out.push([report[0], Math.min(source[0], report[1])])
  if (source[1] < report[1]) out.push([Math.max(source[1], report[0]), report[1]])
  return out.filter(([a, b]) => b > a)
}

/** Самое узкое окно приближения, с: 10 минут. */
export const MIN_SPAN = 600

/** Окно просмотра в пределах окна отчёта, не уже `MIN_SPAN`. */
export function clampView(view: [number, number], full: [number, number]): [number, number] {
  const span = Math.min(Math.max(view[1] - view[0], MIN_SPAN), full[1] - full[0])
  let t0 = Math.max(full[0], Math.min(view[0], full[1] - span))
  if (!Number.isFinite(t0)) t0 = full[0]
  return [t0, t0 + span]
}

/** Масштаб вокруг момента `at`: `factor` < 1 — приблизить. */
export function zoomAt(view: [number, number], full: [number, number], at: number,
                       factor: number): [number, number] {
  const a = Math.min(Math.max(at, view[0]), view[1])
  return clampView([a - (a - view[0]) * factor, a + (view[1] - a) * factor], full)
}

/** Сдвиг окна просмотра на `dt` секунд, в пределах окна отчёта. */
export function panBy(view: [number, number], full: [number, number], dt: number): [number, number] {
  return clampView([view[0] + dt, view[1] + dt], full)
}

/** Подпись соседа: имя режется, расстояние — никогда. */
export function neighborLabel(name: string | null, mmsi: number, km: string, room: number): string {
  const tail = ` (${mmsi}), ${km}`
  const who = name ?? ''
  const left = Math.max(4, room - tail.length)
  return `↳ ${who.length > left ? `${who.slice(0, left - 1)}…` : who}${name ? tail : `${mmsi}, ${km}`}`
}

/**
 * Скрыть или вернуть источник на диаграмме. Последний видимый не скрывается:
 * пустая диаграмма читалась бы как «точек нет».
 */
export function toggleSource(hidden: ReadonlySet<number>, no: number,
                             all: readonly number[]): ReadonlySet<number> {
  if (hidden.has(no)) {
    const n = new Set(hidden)
    n.delete(no)
    return n
  }
  if (all.filter(x => x !== no && !hidden.has(x)).length === 0) return hidden
  return new Set(hidden).add(no)
}
